import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

const rubikfont = { fontFamily: "Rubik, sans-serif" };

// The card used on the home page for products, combo packs and special deals. Everything sits inside one
// white rounded card: the picture (with a badge and an optional heart), the name and price, and an
// "Add to cart" button along the bottom.
//
// image      the picture (any node that fills its frame; give an <img> the class "soft-img" for the hover zoom)
// badge      text for the pill at the top left
// topRight   optional node at the top right (the heart)
// action     { label, icon, onClick, loading, disabled } the button along the bottom
// kicker     small line above the name (brand, "Combo pack", ...)
// title      the name, two lines at most
// children   the price row
const SoftCard = ({ image, badge, topRight, action, kicker, title, children, onClick, width, outOfStock = false }) => (
  <Box
    onClick={onClick}
    sx={{
      width: width || "100%",
      height: "100%",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      cursor: "pointer",
      bgcolor: "#fff",
      borderRadius: "20px",
      border: "1px solid #efe6d0",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(60,40,10,0.07)",
      transition: "box-shadow 0.35s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease",
      "&:hover": { boxShadow: "0 16px 36px rgba(60,40,10,0.16)", transform: "translateY(-4px)", borderColor: "#e3d2a8" },
      "&:hover .soft-img": { transform: "scale(1.06)" },
    }}
  >
    <Box sx={{ position: "relative", aspectRatio: "1 / 1", bgcolor: "#f6f1e7", overflow: "hidden", flexShrink: 0 }}>
      <Box sx={{ position: "absolute", inset: 0, "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)", filter: outOfStock ? "grayscale(55%)" : "none" } }}>
        {image}
      </Box>

      {badge && !outOfStock && (
        <Box sx={{ position: "absolute", top: 10, left: 10, zIndex: 3, px: 1.25, py: 0.4, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.95)", color: "#8a6a24", fontSize: "11px", fontWeight: 700, letterSpacing: "0.3px", boxShadow: "0 2px 8px rgba(0,0,0,0.10)", ...rubikfont }}>
          {badge}
        </Box>
      )}

      {topRight && <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 3 }}>{topRight}</Box>}

      {outOfStock && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 4, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.55)" }}>
          <Box sx={{ px: 1.75, py: 0.7, borderRadius: "999px", bgcolor: "rgba(177,39,4,0.92)", color: "#fff", fontSize: "11px", fontWeight: 700, letterSpacing: "0.6px", ...rubikfont }}>OUT OF STOCK</Box>
        </Box>
      )}
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, p: { xs: 1.25, sm: 1.5 } }}>
      {kicker && (
        <Typography sx={{ fontSize: "10.5px", fontWeight: 600, letterSpacing: "1.5px", color: "#a8832f", textTransform: "uppercase", mb: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...rubikfont }}>
          {kicker}
        </Typography>
      )}
      <Typography sx={{ fontSize: "14.5px", fontWeight: 500, lineHeight: 1.35, color: "#1a1a1a", minHeight: "2.7em", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", ...rubikfont }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mt: 0.6, mb: 1.25 }}>{children}</Box>

      {action && (
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={action.disabled || action.loading || outOfStock}
          startIcon={action.loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : action.icon}
          onClick={(e) => { e.stopPropagation(); action.onClick && action.onClick(e); }}
          sx={{
            mt: "auto",
            py: 0.95,
            borderRadius: "12px",
            textTransform: "none",
            fontSize: "0.88rem",
            fontWeight: 600,
            color: "#fff",
            bgcolor: "#C8A054",
            transition: "background-color 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease",
            "&:hover": { bgcolor: "#b38841", transform: "translateY(-1px)", boxShadow: "0 8px 18px rgba(200,160,84,0.4)" },
            "&.Mui-disabled": { bgcolor: "#e6e0d1", color: "#fff" },
            ...rubikfont,
          }}
        >
          {outOfStock ? "Out of stock" : action.label}
        </Button>
      )}
    </Box>
  </Box>
);

export default SoftCard;

// the price row used by all three cards
export const PriceRow = ({ price, mrp, note }) => (
  <>
    <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1a1a1a", ...rubikfont }}>{price}</Typography>
    {mrp && <Typography sx={{ fontSize: "12.5px", color: "#9a9a9a", textDecoration: "line-through", ...rubikfont }}>{mrp}</Typography>}
    {note && <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2e7d32", ...rubikfont }}>{note}</Typography>}
  </>
);
